import { useEffect, useRef, useState } from "react";
import { useProjectStore } from "../state/projectStore";
import { useKeyStore } from "../state/keyStore";
import { useUi } from "../state/ui";
import { useToast } from "../state/toast";
import { useChordView } from "../hooks/useChordView";
import { useStyledChart } from "../hooks/useStyledChart";
import { useT } from "../i18n";
import { chartText } from "../core/export/chartText";
import { inAppBrowser, kakaoExternalUrl, type InAppKind } from "../core/inAppBrowser";
import { songPathWithKey } from "../core/songUrl";
import { chartMeta, settingLabel } from "./keyText";
import { copyText } from "./copyText";
import { Modal } from "./Modal";
import { PrintSheet } from "./PrintSheet";
import { CopyIcon, DownloadIcon, PrintIcon, ShareIcon } from "./icons";

// "내보내기": PDF download (a file straight away), print (the browser's print dialog) and copy as text
// (copies on tap). All use the key · capo and the play style on screen. In-app browsers can't print or save files, so they
// get directions instead.
export function ExportMenu() {
  const t = useT();
  const analysis = useProjectStore((s) => s.analysis);
  const measures = useProjectStore((s) => s.chartMeasures);
  const source = useProjectStore((s) => s.source);
  const headerTempo = useProjectStore((s) => s.headerTempo);
  const displayTitle = useProjectStore((s) => s.displayTitle);
  const fallbackTitle = useProjectStore((s) => s.title);
  const transpose = useKeyStore((s) => s.transpose);
  const capo = useKeyStore((s) => s.capo);
  const diagrams = useUi((s) => s.printDiagrams);
  const setDiagrams = useUi((s) => s.setPrintDiagrams);
  const view = useChordView();
  const styled = useStyledChart();
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState<{ kind: "text"; select: boolean } | { kind: "inapp"; app: InAppKind } | null>(null);
  const [print, setPrint] = useState(0);   // > 0: print sheet mounted (bump to print again)
  const [pdfBusy, setPdfBusy] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const preRef = useRef<HTMLPreElement>(null);
  const fileBaseRef = useRef("");

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => { if (!wrapRef.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { setOpen(false); btnRef.current?.focus(); } };
    document.addEventListener("pointerdown", onDown);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("pointerdown", onDown); window.removeEventListener("keydown", onKey); };
  }, [open]);

  // the sheet is rendered first, then the browser's print dialog opens; it unmounts after printing.
  // The tab title names the file when the dialog saves a PDF, so it carries this song's name meanwhile.
  useEffect(() => {
    if (!print) return;
    const pageTitle = document.title;
    document.title = fileBaseRef.current || pageTitle;
    const id = requestAnimationFrame(() => window.print());
    const done = () => setPrint(0);
    window.addEventListener("afterprint", done);
    return () => { cancelAnimationFrame(id); window.removeEventListener("afterprint", done); document.title = pageTitle; };
  }, [print]);

  // blocked clipboard → the preview opens with the text selected for a manual copy
  useEffect(() => {
    if (modal?.kind !== "text" || !modal.select || !preRef.current) return;
    const range = document.createRange();
    range.selectNodeContents(preRef.current);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, [modal]);

  if (!analysis || !measures.length) return null;
  const key = { tonic: analysis.key.tonic, quality: analysis.key.quality };
  const setting = { transpose, capo };
  const bpm = headerTempo ?? Math.round(analysis.bpm);
  // read at click time from the store, so a song opened after the last export never lends its name
  const title = displayTitle || (source.kind === "youtube" ? `YouTube ${source.videoId}` : fallbackTitle) || "Tutti";
  const fileBase = `${title} - ${t("export.file_suffix")}`;
  fileBaseRef.current = fileBase;
  const url = source.kind === "youtube" ? location.origin + songPathWithKey(source, setting) : null;
  const footer = `${t("export.footer")}${url ? ` · ${url}` : ""}`;
  // a play style other than 원래대로 travels with the export, named as a suggestion in the header line
  const chart = styled.style !== "orig" ? styled : undefined;
  const styleNote = chart ? ` · ${t("style.meta", { s: t("style." + chart.style) })}` : "";
  const meta = (ascii = false) => chartMeta(t, key, setting, bpm, ascii) + styleNote;
  const name = (label: string, m: number, s: number) => chart?.at(m, s)?.name ?? view.name(label);
  const text = () => chartText({ title, meta: meta(true), measures, name, url: url ?? undefined });

  const blockedInApp = () => {
    const app = inAppBrowser(navigator.userAgent);
    if (app) setModal({ kind: "inapp", app });
    return !!app;
  };
  const startPrint = () => {
    setOpen(false);
    if (!blockedInApp()) setPrint((n) => n + 1);
  };
  const downloadPdf = async () => {
    setOpen(false);
    if (blockedInApp() || pdfBusy) return;
    setPdfBusy(true);
    try {
      const { renderChartPdf, downloadBlob, fileName } = await import("../export/chartPdf");
      const blob = await renderChartPdf({
        title, meta: meta(), capoLabel: capo ? t("key.capo_tag", { n: capo }) : null,
        footer, diagrams, measures, view, chart,
      });
      downloadBlob(blob, fileName(fileBase, "pdf"));
      useToast.getState().show(t("export.pdf_done"));
    } catch {
      useToast.getState().show(t("export.pdf_fail"));
    } finally {
      setPdfBusy(false);
    }
  };
  const copy = async () => {
    setOpen(false);
    setModal(null);
    if (await copyText(text())) {
      useToast.getState().show(t("export.copied"), { label: t("export.view"), run: () => setModal({ kind: "text", select: false }) });
    } else {
      setModal({ kind: "text", select: true });
      useToast.getState().show(t("export.copy_blocked"));
    }
  };
  const closeModal = () => setModal(null);

  return (
    <div className="export-wrap" ref={wrapRef}>
      <button ref={btnRef} className="export-btn" aria-haspopup="true" aria-expanded={open} aria-controls="export-menu" disabled={pdfBusy} onClick={() => setOpen(!open)}>
        {pdfBusy ? <span className="spinner" aria-hidden="true" /> : <ShareIcon size={16} />}<span>{pdfBusy ? t("export.pdf_busy") : t("export.btn")}</span>
      </button>
      {open && (
        <div id="export-menu" className="export-menu">
          <p className="em-note">{t("export.note", { s: settingLabel(t, key, setting) + styleNote })}</p>
          <button className="em-item" onClick={downloadPdf}>
            <span className="em-ic"><DownloadIcon size={17} /></span>
            <span className="em-txt"><b>{t("export.pdf")}</b><small>{t("export.pdf_sub")}</small></span>
          </button>
          <button className="em-item" onClick={startPrint}>
            <span className="em-ic"><PrintIcon size={17} /></span>
            <span className="em-txt"><b>{t("export.print")}</b><small>{t("export.print_sub")}</small></span>
          </button>
          <label className="em-opt">
            <span>{t("export.diagrams")}</span>
            <input type="checkbox" className="switch" checked={diagrams} onChange={(e) => setDiagrams(e.target.checked)} />
          </label>
          <div className="em-sep" />
          <button className="em-item" onClick={copy}>
            <span className="em-ic"><CopyIcon size={16} /></span>
            <span className="em-txt"><b>{t("export.text")}</b><small>{t("export.text_sub")}</small></span>
          </button>
        </div>
      )}

      {print > 0 && (
        <PrintSheet title={title} meta={meta()} capo={capo} diagrams={diagrams} measures={measures} view={view} chart={chart} url={url} />
      )}

      {modal?.kind === "text" && (
        <Modal title={t("export.preview_title")} onClose={closeModal}
          footer={<button className="btn-pri" onClick={copy}><CopyIcon size={15} /> {t("export.copy_again")}</button>}>
          <pre className="export-pre" ref={preRef}>{text()}</pre>
        </Modal>
      )}

      {modal?.kind === "inapp" && (
        <Modal title={t(modal.app === "kakaotalk" ? "export.inapp_title_kakao" : "export.inapp_title")} onClose={closeModal}
          footer={<>
            <button className="tt-btn" onClick={copy}>{t("export.text")}</button>
            {modal.app === "kakaotalk" && <a className="btn-pri" href={kakaoExternalUrl(url ?? location.href)}>{t("export.inapp_open")}</a>}
          </>}>
          <p>{t("export.inapp_body", { s: settingLabel(t, key, setting) })}</p>
          {modal.app !== "kakaotalk" && <p>{t("export.inapp_menu")}</p>}
          <p>{t("export.inapp_text")}</p>
        </Modal>
      )}
    </div>
  );
}
